import { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../lib/notificationsApi";
import type { AppNotification } from "../../lib/notificationsApi";
// import { enablePush, onPushMessage, syncPush } from "../../lib/push";
// import type { PushState } from "../../lib/push";
import type { PushState } from "../../lib/push";
import { toast } from "../../lib/toast";
import { playNotificationSound } from "../../lib/notificationSound";
import { cn } from "../../lib/format";
import { useLang, useT } from "../../i18n";
import type { Lang } from "../../i18n";

const POLL_MS = 30_000;

/** "5 min ago" / "il y a 5 min", then the date after a week. */
function timeAgo(iso: string, lang: Lang, justNow: string): string {
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return justNow;
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "short" });
  if (seconds < 3600) return rtf.format(-Math.floor(seconds / 60), "minute");
  if (seconds < 86400) return rtf.format(-Math.floor(seconds / 3600), "hour");
  if (seconds < 7 * 86400) return rtf.format(-Math.floor(seconds / 86400), "day");
  return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short" });
}

/** Bell in the driver/company headers: unread badge, latest notifications, push opt-in. */
export default function NotificationBell({ className }: { className?: string }) {
  const t = useT();
  const n = t.notifications;
  const lang = useLang();
  const navigate = useNavigate();
  const root = useRef<HTMLDivElement>(null);

  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);
  const [push, setPush] = useState<PushState>("unsupported");
  // Last count from the server: a higher one means something new arrived (sound)
  const lastUnread = useRef<number | null>(null);
  const updateUnread = useCallback((count: number) => {
    if (lastUnread.current !== null && count > lastUnread.current) playNotificationSound();
    lastUnread.current = count;
    setUnread(count);
  }, []);

  const refreshCount = useCallback(() => {
    getUnreadCount().then(updateUnread, () => {
      // Keep the last number (offline, expired session handled elsewhere)
    });
  }, [updateUnread]);

  const load = useCallback(async (before?: string) => {
    setError(false);
    try {
      const page = await getNotifications(before);
      setItems((list) => (before && list ? [...list, ...page.items] : page.items));
      setHasMore(page.items.length === 20);
      updateUnread(page.unread);
    } catch {
      setError(true);
    }
  }, [updateUnread]);

  // Unread count: now, every 30 s and when the tab comes back
  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    const onFocus = () => document.visibilityState === "visible" && refreshCount();
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshCount]);

  // An already-allowed browser stays registered; live pushes refresh the bell
  // useEffect(() => {
  //   void syncPush();
  //   pushState().then(setPush);
  //   return onPushMessage((message) => {
  //     toast.info(message.body ? `${message.title}: ${message.body}` : message.title);
  //     refreshCount();
  //     if (open) void load();
  //   });
  // }, [refreshCount, load, open]);
useEffect(() => {
  let active = true;
  let unsubscribe: (() => void) | undefined;

  void import("../../lib/push").then(async (push) => {
    await push.syncPush();

    const state = await push.pushState();
    if (active) setPush(state);

    unsubscribe = push.onPushMessage((message) => {
      if (!active) return;

      toast.info(
        message.body
          ? `${message.title}: ${message.body}`
          : message.title
      );

      refreshCount();

      if (open) void load();
    });
  });

  return () => {
    active = false;
    unsubscribe?.();
  };
}, [refreshCount, load, open]);
  // Close on outside click and Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // const toggle = () => {
  //   const next = !open;
  //   setOpen(next);
  //   if (next) {
  //     void load();
  //     pushState().then(setPush);
  //   }
  // };
  const toggle = () => {
  const next = !open;
  setOpen(next);

  if (next) {
    void load();

    void import("../../lib/push").then((push) =>
      push.pushState().then(setPush)
    );
  }
};

  const openItem = async (item: AppNotification) => {
    setOpen(false);
    if (!item.read) {
      setItems((list) => list?.map((x) => (x.id === item.id ? { ...x, read: true } : x)) ?? list);
      markNotificationRead(item.id).then(updateUnread, () => {});
    }
    if (item.link) navigate(item.link);
  };

  const readAll = async () => {
    setItems((list) => list?.map((x) => ({ ...x, read: true })) ?? list);
    updateUnread(0);
    await markAllNotificationsRead().catch(() => refreshCount());
  };

  // const turnOnPush = async () => {
  //   const ok = await enablePush();
  //   setPush(await pushState());
  //   if (ok) toast.success(n.pushOn);
  // };
  const turnOnPush = async () => {
  const push = await import("../../lib/push");
  const ok = await push.enablePush();

  setPush(await push.pushState());

  if (ok) toast.success(n.pushOn);
};

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        type="button"
        onClick={toggle}
        aria-label={n.open(unread)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded border-0 bg-surface text-ink transition-colors hover:bg-surface-hover"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={n.title}
          className="fixed inset-x-4 top-16 z-50 flex max-h-[min(520px,calc(100vh-88px))] flex-col overflow-hidden rounded-card border border-line-strong bg-white shadow-lg sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-2 sm:w-90"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <span className="text-sm font-medium text-ink">{n.title}</span>
            {unread > 0 && (
              <button type="button" onClick={() => void readAll()} className="cursor-pointer border-0 bg-transparent p-0 text-xs text-brand-link hover:underline">
                {n.markAll}
              </button>
            )}
          </div>

          {push === "default" && (
            <div className="flex items-start gap-3 border-b border-line bg-tone-blue-bg px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-ink">{n.enablePush}</div>
                <div className="mt-0.5 text-xs text-muted">{n.enablePushHint}</div>
              </div>
              <button
                type="button"
                onClick={() => void turnOnPush()}
                className="h-8 shrink-0 cursor-pointer rounded border-0 bg-brand px-3 text-xs font-medium text-white hover:bg-brand-dark"
              >
                {n.enable}
              </button>
            </div>
          )}
          {push === "denied" && <div className="border-b border-line px-4 py-2.5 text-xs text-muted">{n.pushBlocked}</div>}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {error ? (
              <p className="m-0 px-4 py-8 text-center text-sm text-tone-red-fg">{n.loadError}</p>
            ) : items === null ? (
              <div className="grid gap-2 p-4" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded bg-surface" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <p className="m-0 px-4 py-10 text-center text-sm text-muted">{n.empty}</p>
            ) : (
              <ul className="m-0 list-none p-0">
                {items.map((item) => (
                  <li key={item.id} className="border-b border-line last:border-b-0">
                    <button
                      type="button"
                      onClick={() => void openItem(item)}
                      className={cn(
                        "flex w-full cursor-pointer gap-2.5 border-0 px-4 py-3 text-left hover:bg-[#FAFAFA]",
                        item.read ? "bg-white" : "bg-[#F7F9FE]",
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", item.read ? "bg-transparent" : "bg-brand")}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={cn("block text-[13px] text-ink", !item.read && "font-medium")}>{item.title}</span>
                        {item.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted">{item.body}</span>}
                        <span className="mt-1 block text-[11px] text-faint">{timeAgo(item.createdAt, lang, n.justNow)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {hasMore && items && (
              <button
                type="button"
                onClick={() => void load(items[items.length - 1].createdAt)}
                className="block w-full cursor-pointer border-0 border-t border-line bg-white py-2.5 text-xs font-medium text-brand-link hover:bg-[#FAFAFA]"
              >
                {n.loadMore}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
